import React from 'react';
import {
  Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, LinearProgress, TextField,
} from '@material-ui/core';
import { Helmet } from 'react-helmet';
import i18n from '@i18n';
import { useRoles } from '@hooks';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Alert, Subtitle, Divider,
  ButtonAdorned,
} from '@components/atoms';
import { useSnackbar } from 'notistack';

const initialRol = {
  rolName: '',
  permissions: [{
    modulo: 'solicitudes',
    permisos: [{
      id: 'create',
      label: 'Crear',
      enabled: false,
    },
    {
      id: 'read',
      label: 'Leer',
      enabled: false,
    },
    {
      id: 'update',
      label: 'Actualizar',
      enabled: false,
    }],
  },
  {
    modulo: 'presupuesto',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  {
    modulo: 'analisis',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  {
    modulo: 'facturacion',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  {
    modulo: 'informe',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  {
    modulo: 'admin',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  {
    modulo: 'roles',
    permisos: [
      {
        id: 'create',
        label: 'Crear',
        enabled: false,
      },
      {
        id: 'read',
        label: 'Leer',
        enabled: false,
      },
      {
        id: 'update',
        label: 'Actualizar',
        enabled: false,
      },
    ],
  },
  ],
};

const Roles = () => {
  const { pages: { employees: { roles: texts } } } = i18n;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { rolesError, isRolesLoading, createRole } = useRoles();

  const form = useFormik({
    initialValues: initialRol,
    enableReinitialize: false,
    validateOnChange: true,
    onSubmit: async (values) => {
      const formated = formatedValues(values);
      const created = await createRole(formated);
      if (created) {
        enqueueSnackbar(texts.create.success, { variant: 'success' });
        history.push('/e/roles');
      } else {
        enqueueSnackbar(texts.create.error, { variant: 'error' });
      }
    },
  });
  const formatedValues = (data) => ({
    name: data.rolName,
    permissions: Object.fromEntries(
      data.permissions.map(({ modulo, permisos }) => [
        modulo,
        {
          create: permisos.find((p) => p.id === 'create')?.enabled ?? false,
          read: permisos.find((p) => p.id === 'read')?.enabled ?? false,
          update: permisos.find((p) => p.id === 'update')?.enabled ?? false,
          delete: permisos.find((p) => p.id === 'delete')?.enabled ?? false,
        },
      ]),
    ),
  });
  const togglePermission = (moduloId, permisoId) => {
    const updatedPermissions = form.values.permissions.map((modulo) => (modulo.modulo === moduloId
      ? {
        ...modulo,
        permisos: modulo.permisos.map((permiso) => (permiso.id === permisoId
          ? {
            ...permiso,
            enabled: !permiso.enabled,
          }
          : permiso)),
      }
      : modulo));

    form.setFieldValue('permissions', updatedPermissions);
  };
  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <>
      {isRolesLoading && <LinearProgress /> }
      {rolesError && <Alert>{rolesError.message}</Alert>}
      <Helmet>
        <title>{`${i18n.appName} - ${texts.title}`}</title>
      </Helmet>
      <Grid container spacing={2}>
        <Grid item xs={12} align="left">
          <Subtitle>{texts.create.subtitle}</Subtitle>
        </Grid>
        <Grid item xs={12} align="left" style={{ padding: 0, margin: 0 }}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <TextField
              name="rolName"
              label={texts.create.description}
              placeholder={texts.create.description}
              variant="outlined"
              value={form.values.rolName}
              onChange={form.handleChange}
              error={form.touched.rolName && Boolean(form.errors.rolName)}
              helperText={form.touched.rolName && form.errors.rolName}
              autoComplete="new-create-description"
            />
          </FormControl>
        </Grid>
        <Grid container spacing={1}>
          {form.values.permissions.map((modulo) => (
            <Grid key={modulo.modulo} item xs={12}>
              <FormControl style={{ flexDirection: 'row' }}>
                <FormLabel style={{ alignSelf: 'center', minWidth: 120 }}>{capitalizeFirstLetter(modulo.modulo)}</FormLabel>
                {
                  modulo.permisos.map((permiso) => (
                    <FormGroup key={permiso.id} style={{ minWidth: 100 }}>
                      <FormControlLabel
                        control={<Checkbox name={permiso.label} checked={permiso.enabled} onChange={() => togglePermission(modulo.modulo, permiso.id)} />}
                        label={permiso.label}
                      />
                    </FormGroup>
                  ))
                }
              </FormControl>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <ButtonAdorned color="default" onClick={() => history.push('/e/roles')}>
            {texts.create.list}
          </ButtonAdorned>
        </Grid>
        <Grid item>
          <form onSubmit={form.handleSubmit}>
            <ButtonAdorned type="submit" disabled={form.values.rolName.length === 0}>
              {texts.create.save}
            </ButtonAdorned>
          </form>
        </Grid>
      </Grid>
    </>
  );
};

export default Roles;
